/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { ChangeEvent } from 'react';

import clsx from 'clsx';
import Autosuggest from 'react-autosuggest';

import defaults from './defaults';
import style from './style.module.less';
import PohLoader from '../../Loader';
import { IPohInputProps } from '../index';

// https://github.com/moroshko/react-autosuggest#installation

const theme = {
  container: 'react-autosuggest__container',
  containerOpen: 'react-autosuggest__container--open',
  input: 'react-autosuggest__input',
  inputOpen: clsx('react-autosuggest__input--open', style.acInput),
  inputFocused: 'react-autosuggest__input--focused',
  suggestionsContainer: 'react-autosuggest__suggestions-container',
  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
  suggestionsList: clsx('dropdown-menu show', style.acList),
  suggestion: clsx('dropdown-item', style.acListItem),
  suggestionFirst: 'react-autosuggest__suggestion--first',
  suggestionHighlighted: 'active',
  sectionContainer: 'react-autosuggest__section-container',
  sectionContainerFirst: 'react-autosuggest__section-container--first',
  sectionTitle: 'react-autosuggest__section-title',
};

interface IAutocompleteDataItem {
  text: string;
}

interface IAutocompleteProps {
  data: (() => IAutocompleteDataItem) | IAutocompleteDataItem;
  allowCustom?: boolean;
  findFunction?: (query: string) => (i: IAutocompleteDataItem) => boolean;
  renderItem?: () => React.ReactElement<any>;
  className?: string;
  id?: string;
  value?: any;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  otherInputProps?: Partial<IPohInputProps>;
  minLength?: number;
  debounce?: number;
  ref?: React.RefObject<HTMLInputElement | null>;
}

let debounceTimer: ReturnType<typeof setTimeout>;

const Autocomplete: React.FC<IAutocompleteProps> = ({
  data,
  findFunction = defaults.findFunction,
  renderItem = defaults.renderItem,
  allowCustom = false,
  debounce = 350,
  otherInputProps,
  className,
  value,
  id,
  onBlur,
  onChange,
  minLength = 0,
  ref,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [selectedVal, setSelectedVal] = React.useState<IAutocompleteDataItem | null>(null);
  const [val, setVal] = React.useState(value);

  Object.assign(theme, { input: className });

  const handleSuggestionsFetchRequested = ({ value: query }: { value: string }) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      if (Array.isArray(data)) {
        if (data.length && !data.every((x) => !!x.text)) {
          throw new Error('data for autocomplete expect { text: ... }');
        }
        setSuggestions(data.filter(findFunction(query)));
      } else if (typeof data === 'function' || data?.constructor?.name === 'AsyncFunction') {
        setIsLoading(true);
        try {
          // @ts-ignore
          const dt = await data(query);

          if (!Array.isArray(dt)) {
            throw new Error('data for autocomplete expect array');
          } else if (dt.length && !dt.every((x) => !!x.text)) {
            throw new Error('data for autocomplete expect { text: ... }');
          }

          setSuggestions(dt);
        } catch (_e) {
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        throw new Error('data or getData is not implemented');
      }
    }, debounce);
  };
  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };
  const handleSuggestionSelected = (
    event: React.ChangeEvent<HTMLInputElement>,
    { suggestion }: { suggestion: IAutocompleteDataItem },
  ) => {
    setVal(suggestion.text);
    setSelectedVal(suggestion);
    // @ts-ignore
    onChange?.({ ...event, target: { ...event.target, value: suggestion || val } });
  };
  const handleBlur: React.FocusEventHandler = (event) => {
    let useSelectedVal = !!selectedVal;
    if (selectedVal && selectedVal.text !== val) {
      if (!allowCustom) {
        setVal(selectedVal?.text);
      } else {
        setSelectedVal(null);
        useSelectedVal = false;
      }
    }

    // @ts-ignore
    onBlur?.({
      ...event,
      // @ts-ignore
      target: { ...event.target, value: (useSelectedVal ? selectedVal?.text : val) as string },
    });
  };
  const getSuggestionValue = ({ text }: IAutocompleteDataItem): string => text;
  const renderSuggestion = renderItem;

  const shouldRenderSuggestions = (v: string) => v.trim().length >= minLength;

  // @ts-ignore
  return (
    <div className={style.autocompleteWrapper}>
      {/* @ts-ignore */}
      <Autosuggest
        suggestions={suggestions}
        {
          /** @ts-ignore **/ ...{}
        }
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        {
          /** @ts-ignore **/ ...{}
        }
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestion={renderSuggestion}
        theme={theme}
        inputProps={{
          id,
          value: val,
          type: 'search',
          ...otherInputProps,
          onChange: (
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            event: React.ChangeEvent<HTMLInputElement>,
            { newValue }: { newValue: string },
          ) => setVal(newValue),
          onBlur: handleBlur,
          ref,
        }}
      />
      {isLoading && <PohLoader className={style.loader} />}
    </div>
  );
};

export default Autocomplete;
